import clsx from 'clsx';
import { MouseEvent } from 'react';
import { useToggle } from 'react-use';
import { MinRowsForUsingVirtuoso } from 'src/components/Accordions';
import { multiselectEventPageDetails } from 'src/components/Accordions/InventoryEventsAccordion/InventoryEventsAccordion.css';
import { InventoryEventsBody } from 'src/components/Accordions/InventoryEventsAccordion/InventoryEventsBody';
import { MultiSelectActionBar } from 'src/components/common/MultiSelect/MultiSelectActionBar';
import { InventoryEventBulkActions } from 'src/components/Events/EventListing/InventoryEventListing/BulkActions/InventoryEventBulkActions/InventoryEventBulkActions';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ColumnSettingsModal } from 'src/modals/ColumnSettings/ColumnSettings';
import { LayoutIcon } from 'src/svgs/LayoutIcon';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { SectionType } from 'src/utils/types/sectionType';
import { EventWithData, Feature, Listing } from 'src/WebApiController';

import {
  EventAccordionItemProps,
  EventsAccordion,
  VirtualizedEventAccordionProps,
} from '../EventsAccordion';
import { EventsAccordionV2 } from '../EventsAccordion/EventsAccordionV2';
import { EventListingHeader } from './EventListingHeader';

export const translateEventToInventoryAccordionItem = (
  props: EventWithData,
  index: number,
  isInventoryEventDetailsPage = false
) => {
  return {
    index,
    itemId: props.event.virtId,
    HeaderComponent: EventListingHeader,
    BodyComponent: InventoryEventsBody,
    AfterHeaderComponent: ({
      displayMode,
    }: {
      displayMode?:
        | 'full'
        | 'count-only'
        | 'count-only-button'
        | 'checkbox-only';
    }) => {
      const hasMobileInventoryPageFeature = useUserHasFeature(
        Feature.MobileInventoryEventPage
      );
      const isMobileLayout =
        useMatchMedia('mobile') && hasMobileInventoryPageFeature;

      return isMobileLayout ? null : (
        <MultiSelectActionBar
          groupId={props.event.virtId}
          className={clsx({
            [multiselectEventPageDetails]: displayMode !== 'count-only',
          })}
          displayMode={displayMode}
        >
          <InventoryEventBulkActions
            eventWithData={props}
            listingCount={props.counts.listCnt ?? 0}
            isEventPage={isInventoryEventDetailsPage}
          />
        </MultiSelectActionBar>
      );
    },
    usingVirtuoso:
      isInventoryEventDetailsPage ||
      (props.counts.listCnt ?? 0) > MinRowsForUsingVirtuoso,
    ...props,
    entities: props.entities.listings ?? [],
    entitiesSummary: props.entities.listingsSummary ?? null,
    entityCount: props.counts.listCnt,
    ungroupedListingCount: props.counts.ungrListCnt,
    listingGroupCount: props.counts.listGrpCnt,
  } as EventAccordionItemProps<Listing>;
};

export const InventoryEventsAccordion = (
  props: VirtualizedEventAccordionProps<Listing>
) => {
  const [isColumnModalOpen, toggleColumnModal] = useToggle(false);
  const hasListingTableV2Feature = useUserHasFeature(Feature.ListingTableV2);

  const onColumnSettingButtonClickHandler = (
    e: MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    if (!isColumnModalOpen) toggleColumnModal(true);
  };

  return (
    <>
      {isColumnModalOpen ? (
        <ColumnSettingsModal<CustomListingColumn>
          sectionType={SectionType.Listings}
          onClose={toggleColumnModal}
        />
      ) : null}
      {hasListingTableV2Feature ? (
        <EventsAccordionV2
          {...props}
          tableActions={
            <>
              <Button
                variant="textPlain"
                onClick={onColumnSettingButtonClickHandler}
              >
                <LayoutIcon size={vars.iconSize.m} />
                <Content id={ContentId.Columns} />
              </Button>
            </>
          }
        />
      ) : (
        <EventsAccordion
          {...props}
          tableActions={
            <>
              <Button
                variant="textPlain"
                onClick={onColumnSettingButtonClickHandler}
              >
                <LayoutIcon size={vars.iconSize.m} />
                <Content id={ContentId.Columns} />
              </Button>
            </>
          }
        />
      )}
    </>
  );
};
